import type { ServiceContent } from "@/types";

/**
 * Services offered. Each entry drives both the services overview cards and
 * the individual service detail pages, edit content here rather than in
 * page components.
 *
 * `confirmed: false` marks services that depend on equipment, capacity or
 * arrangements still being finalised. These render with a visible
 * "availability to be confirmed" note and are excluded from Service
 * structured data until confirmed.
 */
export const services: ServiceContent[] = [
  {
    slug: "concrete-cutting",
    name: "Concrete Cutting",
    shortDescription:
      "Controlled cutting for openings, alterations and removals in slabs, walls and structural concrete.",
    heroImageAlt: "Operator saw cutting precast concrete panels on a Melbourne site",
    photoKey: "saw-cutting",
    whatItIs:
      "Precise, controlled cutting of concrete slabs, walls and other structural concrete using diamond blades matched to the material and the job. It covers everything from a single opening to ongoing cutting across a larger project.",
    detail: [
      "Concrete cutting isn't one technique, the right approach depends on what's being cut and where. Flat (floor) sawing handles horizontal slabs, a track-mounted wall saw handles vertical or inclined surfaces, and a hand-held saw gets into tight or awkward spots. Whichever method suits the job, the goal is the same: a clean, straight, accurate cut with as little mess and disruption as possible.",
      "Most cutting is done wet, water runs onto the blade to keep it cool, protect the diamond segments and hold down dust. On indoor or occupied sites that also means managing the slurry so the area is left clean rather than coated in wet concrete dust. Reinforced concrete isn't a problem; diamond blades cut straight through steel mesh and bar.",
    ],
    methods: [
      { name: "Flat / floor sawing", description: "Walk-behind saw for horizontal slabs, paths and driveways, with cut depth set to suit the job." },
      { name: "Wall sawing", description: "A circular blade on a track fixed to the wall, for vertical and inclined cuts a floor saw can't reach." },
      { name: "Hand sawing", description: "Handheld diamond saw for smaller cuts, finishing and confined or hard-to-reach areas." },
    ],
    commonApplications: [
      "Creating openings for windows, doors or new access points",
      "Cutting back damaged or unwanted sections of a slab or path",
      "Preparing clean edges ahead of demolition or removal",
      "Alteration work as part of a renovation or extension",
    ],
    whoTypicallyNeedsIt: [
      "Homeowners renovating or extending",
      "Builders and construction crews",
      "Concreters needing a clean cut line before pouring or removal",
    ],
    neededForQuote: [
      "Photos of the area to be cut",
      "Approximate slab or wall thickness, if known",
      "Length and depth of the cut required",
      "Site access details (driveway, stairs, tight access, etc.)",
    ],
    customerPrep: [
      "Clear the work area of furniture, vehicles or stored items where possible",
      "Note any known services (power, water, gas) running through the area",
      "Let us know if the area needs to be left ready for another trade",
    ],
    safetyConsiderations: [
      "Site assessment before cutting begins",
      "Checking for known services in the cutting path",
      "Water suppression to manage dust during wet cutting",
      "Exclusion zone maintained around active cutting",
    ],
    relatedServiceSlugs: ["core-drilling", "hand-sawing", "concrete-removal"],
    confirmed: true,
  },
  {
    slug: "core-drilling",
    name: "Core Drilling",
    shortDescription:
      "Clean, accurate holes through concrete for plumbing, electrical, HVAC and fixing points.",
    heroImageAlt: "Operator core drilling through concrete with pipework ready for the penetration",
    photoKey: "core-drilling",
    whatItIs:
      "Diamond core drilling produces a clean, round hole through concrete or masonry at a specified diameter and depth, used wherever a service or fixing needs to pass through existing concrete.",
    detail: [
      "Core drilling uses a rotating diamond-tipped barrel to cut a clean, round hole through concrete, brick or block. Holes can go through a slab, wall or footing, straight through or to a set depth, and on the horizontal, vertical or an angle depending on access. Diameters run from small holes for a single conduit up to large cores for stormwater and services.",
      "How the rig is set up depends on the spot. A bolt-down rig anchors to the surface for larger or deeper holes and the cleanest finish; a hand-held drill suits smaller holes and tight access; and where the surface can't be drilled into, a vacuum base holds the rig in place. Coring is done wet, and a suction ring around the bit can catch the slurry so even indoor holes stay tidy.",
    ],
    methods: [
      { name: "Bolt-down rig", description: "Anchored to the slab or wall for larger, deeper holes and precise, clean results." },
      { name: "Hand-held", description: "Free-hand drilling for smaller holes and tight-access spots where a rig won't fit." },
      { name: "Suction-ring slurry control", description: "A vacuum ring around the bit captures water and slurry, keeping indoor and finished areas clean." },
    ],
    commonApplications: [
      "Plumbing and drainage penetrations",
      "Electrical and data conduit penetrations",
      "HVAC and ventilation penetrations",
      "Fixing points and anchor holes",
    ],
    whoTypicallyNeedsIt: [
      "Plumbers needing penetrations for pipework",
      "Electricians running new conduit",
      "Builders and renovators",
      "Commercial and trade contractors",
    ],
    neededForQuote: [
      "Number of holes required",
      "Diameter needed for each hole",
      "Material type (concrete, brick, block) and approximate thickness",
      "Access to both sides of the material, if known",
    ],
    customerPrep: [
      "Mark or describe the exact intended hole locations",
      "Confirm whether holes need to be a specific depth or go all the way through",
      "Flag any known services nearby",
    ],
    safetyConsiderations: [
      "Checking for embedded services before drilling",
      "Water suppression and slurry containment during wet coring",
      "Secure rig setup, particularly for horizontal or overhead coring",
    ],
    relatedServiceSlugs: ["drainage-and-plumbing-penetrations", "concrete-cutting"],
    confirmed: true,
  },
  {
    slug: "floor-and-slab-sawing",
    name: "Floor & Slab Sawing",
    shortDescription:
      "Flat sawing of floors and slabs for openings, alterations and controlled removal sections.",
    heroImageAlt: "Placeholder: floor saw cutting a straight line into a concrete slab",
    whatItIs:
      "Flat sawing (floor sawing) cuts straight lines into horizontal concrete slabs to a set depth, used to section off areas for removal, create openings, or prepare control lines.",
    detail: [
      "Flat sawing uses a walk-behind saw with the blade set to a chosen depth to cut straight lines into horizontal concrete. It's the go-to for sectioning a slab before removal, cutting openings for new services, or scoring clean lines for repair and infill work.",
      "Depth is set to suit the slab and cutting is done wet to cool the blade and control dust. Where a slab is reinforced, the diamond blade cuts through mesh and bar as it goes, so the section comes away cleanly and the edges stay tidy.",
    ],
    commonApplications: [
      "Sectioning a slab ahead of removal",
      "Cutting openings for new plumbing or drainage runs",
      "Creating clean edges for infill or repair work",
    ],
    whoTypicallyNeedsIt: [
      "Builders and concreters",
      "Renovators altering an existing slab",
      "Trade contractors needing access beneath a floor",
    ],
    neededForQuote: [
      "Slab thickness, if known",
      "Length and layout of cuts required",
      "Whether reinforcement (mesh or bar) is expected in the slab",
      "Site access for sawing equipment",
    ],
    customerPrep: [
      "Provide dimensions or a rough sketch of the area",
      "Note any known plumbing, heating or electrical runs below the slab",
    ],
    safetyConsiderations: [
      "Assessment for reinforcement and embedded services",
      "Water suppression and slurry management",
      "Clear exclusion zone while sawing is underway",
    ],
    relatedServiceSlugs: ["concrete-cutting", "concrete-removal"],
    confirmed: true,
  },
  {
    slug: "road-sawing",
    name: "Road Sawing",
    shortDescription:
      "Sawing through road pavement and thicker external slabs for trenching and access.",
    heroImageAlt: "Placeholder: road saw cutting a line through external concrete or asphalt pavement",
    whatItIs:
      "Road sawing cuts through road pavement, driveways or thicker external concrete, typically ahead of trenching for services or planned pavement works.",
    detail: [
      "Road sawing uses a larger, self-propelled saw to cut through road pavement, driveways and thicker external slabs, usually ahead of trenching for services or planned pavement works. The heavier saw and deeper blade get through thicker, tougher pavement than a standard floor saw can manage.",
      "Because this work is often on or near roads and shared access, planning around traffic, pedestrians and water runoff is part of the job. Cutting is done wet, and cut lines are set out to suit the trench or works that follow.",
    ],
    commonApplications: [
      "Trenching for underground services",
      "Driveway and pavement alterations",
      "Council or civil-adjacent works requiring pavement cuts",
    ],
    whoTypicallyNeedsIt: [
      "Civil and construction contractors",
      "Trades installing underground services",
    ],
    neededForQuote: [
      "Pavement type and approximate thickness",
      "Length and layout of cuts required",
      "Site access for larger sawing equipment",
    ],
    customerPrep: [
      "Confirm any required permits or traffic-management arrangements are in place",
      "Provide site dimensions or plans where available",
    ],
    safetyConsiderations: [
      "Site and service assessment before cutting",
      "Traffic and pedestrian management where relevant",
      "Water suppression and runoff control",
    ],
    relatedServiceSlugs: ["floor-and-slab-sawing", "concrete-removal"],
    confirmed: true,
  },
  {
    slug: "hand-sawing",
    name: "Hand Sawing",
    shortDescription:
      "Handheld saw cutting for smaller, tighter-access jobs where larger equipment isn't practical.",
    heroImageAlt: "Placeholder: hand saw making a controlled cut in a tight-access area",
    whatItIs:
      "Hand sawing uses handheld diamond saws for smaller cuts, tight-access areas, or jobs where larger flat or wall saws aren't practical or necessary.",
    detail: [
      "Hand sawing is for the jobs where a big floor or wall saw isn't practical, a single opening, a small alteration, finishing cuts around a larger sawn section, or work in a confined space. A handheld diamond saw is portable and quick to set up, which makes it ideal for one-off residential jobs.",
      "Cut depth with a hand saw is more limited than a track-mounted wall saw, so for thicker walls or deeper openings another method may suit better, that's something we'll flag when scoping the job. Where practical, hand cutting is done wet to keep dust down.",
    ],
    commonApplications: [
      "Individual penetrations and small openings",
      "Renovation cuts in confined spaces",
      "Finishing cuts around larger sawn sections",
    ],
    whoTypicallyNeedsIt: [
      "Homeowners with a single job or small alteration",
      "Renovators and trades working in confined areas",
    ],
    neededForQuote: [
      "Photos of the area and access path",
      "Approximate length and depth of the cut",
    ],
    customerPrep: [
      "Clear a path to the work area where possible",
      "Note power and water access on site",
    ],
    safetyConsiderations: [
      "Water suppression to manage dust where practical",
      "Careful handling in confined or awkward spaces",
    ],
    relatedServiceSlugs: ["concrete-cutting", "expansion-joint-cutting"],
    confirmed: true,
  },
  {
    slug: "expansion-joint-cutting",
    name: "Expansion Joint Cutting",
    shortDescription:
      "Control and expansion joint cutting to manage cracking and movement in concrete.",
    heroImageAlt: "A clean, straight control joint cut into a finished concrete slab",
    photoKey: "slab-cut-line",
    whatItIs:
      "Expansion (control) joints are shallow cuts made into concrete to manage where natural cracking occurs as the slab moves and cures, and to separate sections that need to expand independently.",
    detail: [
      "Control joints (often called expansion joints) are shallow cuts made into a slab so the natural cracking as concrete cures and moves happens along a neat, planned line rather than randomly across the surface. They're typically cut to about a quarter of the slab's depth, in a pattern suited to the slab's size and shape.",
      "Timing matters on a new pour, joints are cut once the slab is firm enough not to ravel, but before uncontrolled cracking starts. Joints can also be retrofitted into existing concrete that's showing early signs of movement.",
    ],
    commonApplications: [
      "New slab pours requiring control joints",
      "Retrofitting joints into existing concrete showing early cracking",
      "Separating new work from existing structures",
    ],
    whoTypicallyNeedsIt: [
      "Concreters and builders",
      "Homeowners with a new driveway, path or slab",
    ],
    neededForQuote: [
      "Slab area and layout",
      "Timing relative to the pour, if the slab is new",
    ],
    customerPrep: [
      "Provide a layout or sketch of the slab if the joint pattern is specific",
    ],
    safetyConsiderations: [
      "Correct timing relative to curing for new slabs",
      "Dust and water management during cutting",
    ],
    relatedServiceSlugs: ["hand-sawing", "concrete-cutting"],
    confirmed: true,
  },
  {
    slug: "drainage-and-plumbing-penetrations",
    name: "Drainage & Plumbing Penetrations",
    shortDescription:
      "Accurate penetrations for drainage, stormwater and plumbing runs through existing concrete.",
    heroImageAlt: "Placeholder: core-drilled penetration prepared for a drainage pipe",
    whatItIs:
      "Purpose-cut or cored penetrations sized and placed for drainage, stormwater or plumbing pipework passing through slabs, footings or walls.",
    detail: [
      "These are penetrations cut or cored specifically for drainage, stormwater and plumbing pipework passing through a slab, footing or wall. The hole is sized to the pipe and set out to match the plumber's layout, whether that's a single waste through a bathroom slab or a larger stormwater penetration.",
      "Most penetrations are core-drilled for a clean round hole, though larger or rectangular openings are saw-cut. Work is done wet with slurry controlled where the area needs to stay clean, and known services are checked before anything is cut.",
    ],
    commonApplications: [
      "New bathroom or laundry drainage points",
      "Stormwater pipe penetrations",
      "Retrofitting plumbing through an existing slab",
    ],
    whoTypicallyNeedsIt: ["Plumbers", "Builders", "Renovators"],
    neededForQuote: [
      "Pipe diameter and required hole size",
      "Location and material thickness",
      "Access to both sides of the penetration point",
    ],
    customerPrep: [
      "Confirm exact pipe location with your plumber before the visit",
      "Flag any known services nearby",
    ],
    safetyConsiderations: [
      "Service location checks before cutting or drilling",
      "Water suppression and slurry containment",
    ],
    relatedServiceSlugs: ["core-drilling", "concrete-cutting"],
    confirmed: true,
  },
  {
    slug: "wall-and-doorway-openings",
    name: "Wall & Doorway Openings",
    shortDescription:
      "New openings cut into concrete or masonry walls for doors, windows and access points.",
    heroImageAlt: "A large new opening cut into a precast concrete wall",
    photoKey: "wall-opening",
    whatItIs:
      "Wall sawing creates new openings, doorways, windows or general access points, in concrete or masonry walls, cut cleanly to the required dimensions.",
    detail: [
      "New openings, doorways, windows or access points, are cut into concrete or masonry walls with a track-mounted wall saw, which gives straight, accurate edges to the marked dimensions. The saw runs on a track fixed to the wall, so it can cut vertically and horizontally and handle thicker walls than a hand saw.",
      "Openings are usually cut as a sequence of edges, then the section is broken out and removed. Where the wall is load-bearing, structural support such as a lintel needs to be planned and in place first, that's coordinated with your builder or engineer before cutting starts.",
    ],
    methods: [
      { name: "Track-mounted wall sawing", description: "A circular blade on a wall-fixed track for straight, accurate vertical and horizontal cuts." },
      { name: "Break-out & removal", description: "The cut section is broken out and removed, leaving a clean opening ready for the next trade." },
    ],
    commonApplications: [
      "New internal doorways during a renovation",
      "Window openings in concrete or masonry walls",
      "Access openings for equipment or services",
    ],
    whoTypicallyNeedsIt: ["Homeowners renovating", "Builders", "Commercial fit-out contractors"],
    neededForQuote: [
      "Wall thickness and material, if known",
      "Opening dimensions required",
      "Whether structural support (e.g. a lintel) is already planned",
    ],
    customerPrep: [
      "Confirm structural requirements with your builder or engineer where relevant",
      "Clear access to both sides of the wall where possible",
    ],
    safetyConsiderations: [
      "Structural assessment and, where needed, temporary support before cutting",
      "Service checks within the wall",
      "Dust and water management",
    ],
    relatedServiceSlugs: ["concrete-cutting", "concrete-removal"],
    confirmed: true,
  },
  {
    slug: "concrete-removal",
    name: "Concrete Removal",
    shortDescription:
      "Removal of cut concrete sections, coordinated with cutting work for a clean finished area.",
    heroImageAlt: "Concrete and masonry being broken out and removed on a commercial site",
    photoKey: "concrete-removal",
    whatItIs:
      "Once concrete has been cut, the resulting sections often need to be broken out and removed to leave the area ready for the next stage. Availability and scope depend on the size of the job and site access.",
    detail: [
      "Once concrete has been cut, the sections often need to be broken out and taken away so the area's ready for the next stage. Depending on size and access, that can mean lifting out sawn slab sections whole, breaking them down on site, and loading them into a skip or removing them offsite.",
      "How much can be removed, and whether offsite disposal is included, depends on the job and the access, narrow side gates, stairs and shared driveways all affect it, so removal is scoped and confirmed per job rather than assumed upfront.",
    ],
    commonApplications: [
      "Clearing a cut slab section for a new pour",
      "Removing concrete after an opening or alteration",
      "Leaving a site ready for the next trade",
    ],
    whoTypicallyNeedsIt: ["Homeowners", "Builders", "Renovators"],
    neededForQuote: [
      "Volume or area of concrete to be removed",
      "Site access for removal (driveway width, gates, stairs)",
      "Whether offsite disposal is required",
    ],
    customerPrep: [
      "Confirm whether a skip bin will be on site or if offsite disposal is needed",
      "Note access constraints (narrow side gates, steps, shared driveways)",
    ],
    safetyConsiderations: [
      "Safe breaking and lifting practices",
      "Site protection for surrounding surfaces",
      "Traffic and pedestrian management where relevant on the street or site",
    ],
    relatedServiceSlugs: ["concrete-cutting", "floor-and-slab-sawing"],
    confirmed: true,
  },
];

export const getServiceBySlug = (slug: string): ServiceContent | undefined =>
  services.find((service) => service.slug === slug);

export const getRelatedServices = (service: ServiceContent): ServiceContent[] =>
  service.relatedServiceSlugs
    .map((slug) => getServiceBySlug(slug))
    .filter((s): s is ServiceContent => Boolean(s));
